// Type-only import: the runtime side of the card pulls in icon modules, which unit tests
// running this pure model have no reason to load.
import { isSentinelQueuedXchainWithdraw, isWithdrawTransfer } from "./transfer-arms";
import type { TransferProgressData } from "./transfer-progress-card";
import { hasSweepAction, isTransferComplete, isTransferDismissible } from "./transfer-terminality";

/** Where the funds ended up once they landed. `null` until they have, and for every withdrawal. */
export type TransferSettlement = "wallet" | "cross-margin" | "spot-only";

/**
 * The one action a card offers, carrying the transfer already narrowed to the arm that action
 * accepts. A value rather than a switch arm, so a branch the container can never reach cannot
 * be written — see the `cctp-deposit` arm this replaced.
 */
export type TransferNextAction =
  | {
      kind: "cctp-claim-and-deposit";
      transfer: TransferProgressData & { type: "cctp-deposit"; sourceTxHash: string };
    }
  | {
      kind: "withdraw-claim";
      transfer: TransferProgressData & { type: "cctp-withdraw"; sourceTxHash: string };
    }
  | {
      kind: "continue-bridge";
      transfer: TransferProgressData & { type: "queued-xchain-withdraw" };
    }
  | {
      kind: "deposit-to-exchange";
      transfer: TransferProgressData & {
        type: "mesh-deposit" | "xchain-deposit" | "fun-deposit";
        subaccountToDepositTo: string;
      };
    };

export interface TransferCapabilities {
  nextAction: TransferNextAction | null;
  /**
   * `isTransferComplete` — nothing left to do. NOT "have the funds landed"; see
   * `use-report-confirmed-transfers.ts`, which deliberately answers the other question.
   */
  isTerminal: boolean;
  isDismissible: boolean;
  settlement: TransferSettlement | null;
}

function selectNextAction(transfer: TransferProgressData): TransferNextAction | null {
  if (
    transfer.type === "cctp-deposit" &&
    (transfer.currentStep === "claimable" || transfer.currentStep === "available")
  ) {
    return { kind: "cctp-claim-and-deposit", transfer };
  }

  if (transfer.type === "cctp-withdraw" && transfer.currentStep === "claimable") {
    return { kind: "withdraw-claim", transfer };
  }

  // Queued x-chain withdrawal: show "Continue Bridge" button once the on-chain withdrawal clears.
  // Skip the sentinel from auto-import — the real chain is unknown, so we can't safely initiate
  // the bridge.
  if (transfer.type === "queued-xchain-withdraw" && transfer.currentStep === "confirming") {
    return isSentinelQueuedXchainWithdraw(transfer) ? null : { kind: "continue-bridge", transfer };
  }

  // A cross-chain deposit only offers the move into cross margin when the exchange can actually
  // credit what arrived — a route can deliver an asset that is not collateral, and the sweep would
  // abort on chain. The funds are already in the subaccount either way, so the card just ends here
  // with its dismiss button instead.
  if (
    (transfer.type === "xchain-deposit" || transfer.type === "fun-deposit") &&
    !hasSweepAction(transfer)
  ) {
    return null;
  }

  // `cctp-deposit` is deliberately absent: the first rule already claims it at both `claimable`
  // and `available`, so listing it here only ever produced an unreachable branch.
  if (
    (transfer.type === "mesh-deposit" ||
      transfer.type === "xchain-deposit" ||
      transfer.type === "fun-deposit") &&
    transfer.currentStep === "available"
  ) {
    return { kind: "deposit-to-exchange", transfer };
  }

  return null;
}

function selectSettlement(transfer: TransferProgressData): TransferSettlement | null {
  // Nothing settles anywhere the card can describe on the way out.
  if (isWithdrawTransfer(transfer)) return null;

  // Landed-only, which is what makes `hasSweepAction(t) === (settlement === "cross-margin")` an
  // identity rather than a coincidence.
  if (transfer.currentStep !== "available") return null;

  if (transfer.type !== "xchain-deposit" && transfer.type !== "fun-deposit") return "wallet";

  // A route can deliver an asset the exchange does not take as collateral
  // (`server/src/providers/oneclick/origin-assets.ts:159`). It is spendable on spot immediately —
  // the wording `deposit.xchainNotCollateral` gives before the deposit, now that it has landed.
  return transfer.sweepable ? "cross-margin" : "spot-only";
}

/**
 * What a card can do with a transfer: the action it offers, whether it is finished, whether the
 * user may close it, and where the funds settled.
 *
 * The single entry point for the UI. A pure function of the record — no memoisation, and no
 * assumption that a card keeps its identity, since `replaceTransfer` swaps one for another of a
 * different type.
 */
export function deriveCapabilities(transfer: TransferProgressData): TransferCapabilities {
  return {
    nextAction: selectNextAction(transfer),
    isTerminal: isTransferComplete(transfer),
    isDismissible: isTransferDismissible(transfer),
    settlement: selectSettlement(transfer),
  };
}
